import React from 'react';
import { Box, Skeleton, Toolbar } from '@mui/material';

const DRAWER_WIDTH = 264;

/**
 * Enterprise-style loading shell shown while the tenant's branding/theme
 * (colors, font, logo) is being resolved, before the real app shell mounts.
 * Mirrors the structure of `Layout` (topbar + sidebar + content) so the
 * transition into the real UI doesn't jump/flash.
 */
function AppSkeleton() {
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Sidebar skeleton */}
      <Box
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          display: { xs: 'none', md: 'block' },
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Toolbar sx={{ px: 3 }}>
          <Skeleton variant="rounded" width="100%" height={40} />
        </Toolbar>
        <Box sx={{ px: 2, pt: 1 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={40} sx={{ mb: 1, borderRadius: 3 }} />
          ))}
        </Box>
        <Box sx={{ p: 2, position: 'absolute', bottom: 0, width: DRAWER_WIDTH - 32 }}>
          <Skeleton variant="rounded" height={72} sx={{ borderRadius: 5 }} />
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {/* Topbar skeleton */}
        <Box
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', gap: 2 }}>
            <Skeleton
              variant="rounded"
              width={{ xs: 0, sm: 320 }}
              height={36}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            />
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Skeleton variant="circular" width={36} height={36} />
              <Skeleton variant="circular" width={36} height={36} />
            </Box>
          </Toolbar>
        </Box>

        {/* Content skeleton */}
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Skeleton variant="text" width={220} height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={340} height={24} sx={{ mb: 3 }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 2,
              mb: 3,
            }}
          >
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={110} sx={{ borderRadius: 4 }} />
            ))}
          </Box>

          <Skeleton variant="rounded" height={320} sx={{ borderRadius: 4, mb: 3 }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
              gap: 2,
            }}
          >
            <Skeleton variant="rounded" height={240} sx={{ borderRadius: 4 }} />
            <Skeleton variant="rounded" height={240} sx={{ borderRadius: 4 }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default AppSkeleton;
